"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useSwiperBreakpoints = exports.useSwiperA11y = exports.manageTabIndex = exports.handleTokenizedString = exports.handleSlideFocus = exports.handleInactiveSwiperSlides = void 0;
var _react = require("react");
var _accessibility = require("../constants/accessibility");
/**
 * This method takes the tokenizedString, values as a parameter,
 * return the updated tokenized string
 * @param  {String} tokenizedString - gets slide %x of %totalLength from CMS
 * @param  {Array} values - [array index count starts with 1, total length of array]
 * @returns {String|null}
 */
const handleTokenizedString = function (tokenizedString) {
  let values = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  if (!tokenizedString || typeof tokenizedString !== 'string') {
    return null;
  }
  if (!values || !Array.isArray(values) || !tokenizedString.includes('%')) {
    return tokenizedString;
  }
  return values.reduce((acc, val, index) => {
    const token = `%${index + 1}`;
    return acc.replace(token, val);
  }, tokenizedString);
};

/**
 * Helper function to manage tabindex
 * @function manageTabIndex
 * @param { HTMLElement } element - element to remove/add to tab index
 * @param { 'set' | 'remove' } action - add or remove action
 */
exports.handleTokenizedString = handleTokenizedString;
const manageTabIndex = (element, action) => {
  if (!element || typeof element.setAttribute !== 'function') {
    return;
  }
  if (action === 'set') {
    element.setAttribute('tabindex', '-1');
  } else if (action === 'remove') {
    // Preserve tabindex on elements that are intentionally hidden from
    // the accessibility tree (e.g. decorative links with aria-hidden="true")
    if (element.getAttribute('aria-hidden') === 'true') {
      return;
    }
    element.removeAttribute('tabindex');
  }
};

/**
 * Function to handle keyboard focus when navigating through the slides with the previous/next buttons
 * @function handleSlideFocus
 * @param { object } data - swiper instance wrapper
 * @param { object } data.swiper - swiper instance
 */
exports.manageTabIndex = manageTabIndex;
const handleSlideFocus = data => {
  const {
    swiper
  } = data;
  const activeIndex = swiper.activeIndex;
  const activeSlide = swiper.slides?.[activeIndex];
  if (activeSlide) {
    // Set tabindex on the slide to make it focusable
    activeSlide.setAttribute('tabindex', '-1');
    // Focus the active slide, preventing the browser from scrolling to it,
    // which can interfere with Swiper's own scroll management.
    activeSlide.focus({
      preventScroll: true
    });
  }
};

/**
 * This method to hide the inactive swiper slides and not to get the focus for child focusable elements
 * Add aria-hidden to true for swipers which are not in viewport
 * Add tabindex to -1 for child focusable elements of swiper slide which are not in viewport
 * @function handleInactiveSwiperSlides
 * @param { object } swiper - swiper instance
 */
exports.handleSlideFocus = handleSlideFocus;
const handleInactiveSwiperSlides = swiper => {
  const {
    slides,
    visibleSlides
  } = swiper || {};
  if (!slides || !visibleSlides) {
    return;
  }
  const focusableSelectors = _accessibility.FOCUSABLE_ELEMENT_SELECTORS.join(', ');
  slides.forEach(item => {
    item.setAttribute('aria-hidden', 'true');
    const focusableElements = item.querySelectorAll(focusableSelectors);
    focusableElements.forEach(el => manageTabIndex(el, 'set'));
  });
  visibleSlides.forEach(item => {
    item.removeAttribute('aria-hidden');
    const focusableElements = item.querySelectorAll(focusableSelectors);
    focusableElements.forEach(el => manageTabIndex(el, 'remove'));
  });
};

/**
 * Hook to manage Swiper accessibility and focus
 * @param {object} swiperInstance - The swiper instance
 * @returns {object} Object containing event handlers
 */
exports.handleInactiveSwiperSlides = handleInactiveSwiperSlides;
const useSwiperA11y = swiperInstance => {
  const handleA11y = (0, _react.useCallback)(() => {
    if (swiperInstance) {
      handleInactiveSwiperSlides(swiperInstance);
    }
  }, [swiperInstance]);
  const handleFocus = (0, _react.useCallback)(() => {
    if (swiperInstance) {
      handleSlideFocus({
        swiper: swiperInstance
      });
    }
  }, [swiperInstance]);
  return {
    handleA11y,
    handleFocus
  };
};

/**
 * Helper to get slidesPerView for a specific breakpoint
 */
exports.useSwiperA11y = useSwiperA11y;
const getSlidesPerView = (breakpoint, data) => {
  const {
    slidesPerViewAuto,
    isHighlighter,
    sliderHighlightPerViewLG,
    sliderPerViewXL,
    sliderPerViewLG,
    sliderPerViewMD,
    sliderPerViewSM
  } = data;
  if (slidesPerViewAuto) return 'auto';
  switch (breakpoint) {
    case 'XL':
      return sliderPerViewXL || 4;
    case 'LG':
      if (isHighlighter) return sliderHighlightPerViewLG;
      return sliderPerViewLG || 3;
    case 'MD':
      return sliderPerViewMD || 3;
    case 'SM':
      return sliderPerViewSM || 1.6;
    default:
      return 1;
  }
};

/**
 * Helper to get slidesPerGroup for a specific breakpoint
 */
const getSlidesPerGroup = (breakpoint, data) => {
  const {
    isHighlighter,
    slidesHighlightPerGroupLG,
    slidesPerGroupXL,
    slidesPerGroupLG,
    slidesPerGroupMD,
    slidesPerGroupSM
  } = data;
  switch (breakpoint) {
    case 'XL':
      return slidesPerGroupXL || 4;
    case 'LG':
      if (isHighlighter) return slidesHighlightPerGroupLG || slidesPerGroupLG || 3;
      return slidesPerGroupLG || 3;
    case 'MD':
      return slidesPerGroupMD || 3;
    case 'SM':
      return slidesPerGroupSM || 1;
    default:
      return 1;
  }
};

/**
 * Hook to generate Swiper breakpoints configuration
 * @param {object} props - Carousel props
 * @returns {object} Swiper breakpoints config
 */
const useSwiperBreakpoints = props => {
  return (0, _react.useMemo)(() => ({
    XL: {
      slidesPerView: getSlidesPerView('XL', props),
      slidesPerGroup: getSlidesPerGroup('XL', props)
    },
    LG: {
      slidesPerView: getSlidesPerView('LG', props),
      slidesPerGroup: getSlidesPerGroup('LG', props)
    },
    MD: {
      slidesPerView: getSlidesPerView('MD', props),
      slidesPerGroup: getSlidesPerGroup('MD', props)
    },
    SM: {
      slidesPerView: getSlidesPerView('SM', props),
      slidesPerGroup: getSlidesPerGroup('SM', props)
    }
  }), [props.slidesPerViewAuto, props.isHighlighter, props.sliderHighlightPerViewLG, props.sliderPerViewXL, props.sliderPerViewLG, props.sliderPerViewMD, props.sliderPerViewSM, props.slidesHighlightPerGroupLG, props.slidesPerGroupXL, props.slidesPerGroupLG, props.slidesPerGroupMD, props.slidesPerGroupSM]);
};
exports.useSwiperBreakpoints = useSwiperBreakpoints;